import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { computeCharge } from '@continuum/show-core';
import type { GuestIntent, ShowState } from '@continuum/show-core';
import { RoomClient } from './client.js';
import type { ConnectionStatus, Refusal, WireState, You } from './client.js';
import type { Session } from './session.js';
import { ChatStore } from '../chat/store.js';

/**
 * Which room to talk to. When the Mac is hosting the app itself on the venue LAN
 * (the offline fallback), the page was served by that same server — so talk back to
 * wherever we were loaded from rather than reaching for the cloud that is down.
 */
function roomOrigin(): string {
  if (typeof window !== 'undefined') {
    const here = window.location;
    const local =
      here.port === '8787' ||
      /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|127\.|localhost$)/.test(here.hostname);
    if (local) return here.origin;
  }
  return import.meta.env.VITE_ROOM_ORIGIN ?? 'https://continuum-room.marek-novak.workers.dev';
}

const ORIGIN = roomOrigin();

export interface Room {
  state: WireState | null;
  you: You | null;
  status: ConnectionStatus;
  /** The drive, recomputed locally every tick — it is never sent over the wire. */
  charge: number;
  lastCue: string | null;
  /** The room will not let this phone in, and why (see client.ts). */
  refused: Refusal | null;
  send: (intent: GuestIntent) => void;
  /** This phone's messages to and from other guests (chat/), kept beside the show state. */
  chat: ChatStore;
  /** The TV follows this guest's game now: their board is to be sent (`sendBoard`) while a game is on. */
  boardOn: boolean;
  sendBoard: (view: unknown) => void;
}

/**
 * Holds the room state for one phone. The charge bar is derived here rather than
 * received: `computeCharge` is deterministic from the clock and the state both
 * sides already hold, so the bar animates smoothly while the room stays silent.
 */
/** What the steward can do to this phone's seat from the console. */
export interface SeatChanges {
  moved: (session: Session) => void;
  freed: () => void;
  /** The room turned something down: the app decides what, if anything, the guest sees. */
  rejected: (error: string, intent: string | null) => void;
}

export function useRoom(session: Session | null, changes?: SeatChanges): Room {
  const changesRef = useRef(changes);
  changesRef.current = changes;
  const [state, setState] = useState<WireState | null>(null);
  /**
   * The private half of this seat — role, volunteer answer, sabotages left, own ballot —
   * arrives on its own channel whenever it moves. Everything else is read off our own
   * seat in the shared state, which keeps updating as patches land.
   */
  const [mine, setMine] = useState<You | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [lastCue, setLastCue] = useState<string | null>(null);
  const [refused, setRefused] = useState<Refusal | null>(null);
  const [boardOn, setBoardOn] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const clientRef = useRef<RoomClient | null>(null);
  const chat = useMemo(
    () =>
      new ChatStore(
        session?.seat ?? 0,
        (to, text) => clientRef.current?.sendChat(to, text) ?? null,
        undefined,
        (to, n) => clientRef.current?.sendChatRead(to, n),
      ),
    [session],
  );

  useEffect(() => {
    if (!session) return;
    setRefused(null);
    const client = new RoomClient(ORIGIN, session, {
      onState: (next, own) => {
        setState(next);
        if (own) setMine(own);
      },
      onPatch: (patch) => setState((prev) => (prev ? { ...prev, ...patch } : prev)),
      onYou: (own) => setMine(own),
      onCue: setLastCue,
      onStatus: setStatus,
      onMoved: (next) => changesRef.current?.moved(next),
      onFreed: () => changesRef.current?.freed(),
      onRejected: (message, intent) => changesRef.current?.rejected(message, intent),
      onRefused: setRefused,
      onChat: (msg) => chat.receive(msg),
      onShowBoard: setBoardOn,
    });
    clientRef.current = client;
    client.connect();
    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [session, chat]);

  // A local clock, so the drive climbs between patches.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const charge = useMemo(() => {
    if (!state) return 0;
    // computeCharge only reads fields the wire state carries.
    return computeCharge(state as unknown as ShowState, now);
  }, [state, now]);

  const send = useCallback((intent: GuestIntent) => clientRef.current?.send(intent), []);
  const sendBoard = useCallback((view: unknown) => clientRef.current?.sendBoard(view), []);

  const you = useMemo<You | null>(() => {
    if (!session || !state) return null;
    const seat = state.seats.find((s) => s.seat === session.seat);
    if (!seat) return null;
    return {
      seat: seat.seat,
      callsign: seat.callsign,
      profession: seat.profession,
      alignment: mine?.alignment ?? null,
      volunteer: mine?.volunteer ?? null,
      brig: seat.brig,
      sabotagesLeft: mine?.sabotagesLeft ?? 0,
      ballot: mine?.ballot ?? null,
      ...(mine?.solved ? { solved: mine.solved } : {}),
      ...(mine?.signal !== undefined ? { signal: mine.signal } : {}),
      ...(mine?.armed !== undefined ? { armed: mine.armed } : {}),
      ...(mine?.saboteurAt ? { saboteurAt: mine.saboteurAt } : {}),
    };
  }, [session, state, mine]);

  return { state, you, status, charge, lastCue, refused, send, chat, boardOn, sendBoard };
}
